import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient, useQueries } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container, Alert } from 'reactstrap';
import { BsBuildings } from 'react-icons/bs';
import {
  FiPlus,
  FiEdit2,
  FiSliders,
  FiUsers,
  FiBarChart2,
} from 'react-icons/fi';

import { appRoutes } from '@/routes';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { StatCard } from '@/components/StatCard';
import { PillDropdown } from '@/components/PillDropdown';
import { Dropdown, dropdownSizes } from '@/components/Dropdown';
import { Button, buttonVariants } from '@/components/Button';
import { Input as CustomInput } from '@/components/Input';
import { Table } from '@/components/Table';
import { Pagination } from '@/components/Pagination';
import { AvatarStack } from '@/components/AvatarStack';
import { EmptyState } from '@/components/EmptyState';
import { Spinner } from '@/components/Spinner/Spinner.component';

import firebase from '@/config/firebase.config';
import { useUserCompanies } from '@/hooks/useUserCompanies';
import teamService from '@/services/team';
import { getTeamMemberDetails } from '@/services/teamMemberDetails';
import { queryKeys } from '@/config/queryKeys';
import { useEmployeePageCompanyTeamUrlSync } from '@/hooks/useEmployeePageUrlSync';
import { useUrlParamSync } from '@/hooks/useUrlParamSync';
import { mergeEmployeePageFiltersIntoSearch } from '@/utils/employeePageUrlFilters';
import { EMPLOYEE_PAGE_QUERY } from '@/constants/urlQuery';
import { AddTeamModal } from '@/views/Teams/components/TeamsTab/AddTeamModal.component';
import { useIsAdmin } from '@/hooks/usePermission';

import { resolveDepartmentIcon } from '@/utils/departmentIcons';
import styles from './TeamList.module.scss';

const database = firebase.firestore();
const PAGE_SIZE = 10;

const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Name (A–Z)' },
  { value: 'name_desc', label: 'Name (Z–A)' },
  { value: 'members_desc', label: 'Members (High–Low)' },
  { value: 'members_asc', label: 'Members (Low–High)' },
];

function countActiveMembers(memberDetailsMap) {
  return Object.entries(memberDetailsMap || {}).filter(
    ([, d]) => d && d.deleted !== true
  ).length;
}

function getActiveMemberNames(memberDetailsMap) {
  return Object.entries(memberDetailsMap || {})
    .filter(([, d]) => d && d.deleted !== true)
    .map(([, d]) => d.userName || d.userEmailid || '?');
}

const noop = () => {};

function sortRows(rows, sortValue) {
  const sorted = [...rows];
  switch (sortValue) {
    case 'name_desc':
      return sorted.sort((a, b) => b.teamName.localeCompare(a.teamName));
    case 'members_desc':
      return sorted.sort((a, b) => (b.memberCount ?? 0) - (a.memberCount ?? 0));
    case 'members_asc':
      return sorted.sort((a, b) => (a.memberCount ?? 0) - (b.memberCount ?? 0));
    case 'name_asc':
    default:
      return sorted.sort((a, b) => a.teamName.localeCompare(b.teamName));
  }
}

function DepartmentCell({ teamName, onClick }) {
  const { Icon, iconColor, iconBg } = resolveDepartmentIcon(teamName);
  return (
    <div
      role="presentation"
      className={styles.departmentCell}
      onClick={onClick}
    >
      <span
        className={styles.departmentIconWrap}
        style={{ backgroundColor: iconBg }}
      >
        <Icon size={16} color={iconColor} />
      </span>
      <Text
        variant={textVariantsEnum.Text_sm}
        fontWeight={fontWeightEnum.Medium}
      >
        {teamName}
      </Text>
    </div>
  );
}

function MembersCell({ loading, memberNames, memberCount, onClick }) {
  if (loading) {
    return (
      <Text variant={textVariantsEnum.Text_sm} color="gray-400">
        …
      </Text>
    );
  }
  return (
    <div role="presentation" className={styles.membersCell} onClick={onClick}>
      <AvatarStack members={memberNames} />
      <Text
        variant={textVariantsEnum.Text_sm}
        fontWeight={fontWeightEnum.Normal}
        color="gray-600"
      >
        {memberCount ?? '—'} {memberCount === 1 ? 'member' : 'members'}
      </Text>
    </div>
  );
}

export const TeamListComponent = () => {
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const authState = useSelector((state) => ({ ...state.auth }));
  const userProfile = authState.profile;
  const companyList = useMemo(
    () => userProfile?.companyList || [],
    [userProfile?.companyList]
  );
  const userUID = userProfile?.id;
  const activeCompanyId = userProfile?.activeCompany?.companyId;

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState(SORT_OPTIONS[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);

  const isAdminUser = useIsAdmin();

  // The role the user actually holds in the *selected* company — not just
  // their session-wide/active-company role — since being Admin somewhere
  // else shouldn't grant the "see every department's members" branch of
  // getMemberTeamCollection for a company they only belong to as a Member.
  const selectedCompanyRole = useMemo(() => {
    if (!selectedCompany?.value) return null;
    if (userProfile?.activeCompany?.companyId === selectedCompany.value) {
      return userProfile.activeCompany.role;
    }
    return (
      companyList.find((c) => c.companyId === selectedCompany.value)?.role ||
      null
    );
  }, [selectedCompany?.value, userProfile?.activeCompany, companyList]);

  const selectedCompanyCompanyRole = useMemo(() => {
    if (!selectedCompany?.value) return null;
    if (userProfile?.activeCompany?.companyId === selectedCompany.value) {
      return userProfile.activeCompany.companyRole;
    }
    return (
      companyList.find((c) => c.companyId === selectedCompany.value)
        ?.companyRole || null
    );
  }, [selectedCompany?.value, userProfile?.activeCompany, companyList]);

  const {
    companyOptions,
    loading: loadingCompanies,
    error: companiesError,
  } = useUserCompanies(userUID);

  const {
    data: rawTeams = [],
    isLoading: loadingTeams,
    isError: teamsQueryError,
    error: teamsErrorObj,
  } = useQuery({
    queryKey: queryKeys.teamList.teamsForCompany(
      userUID,
      selectedCompany?.value
    ),
    queryFn: async () => {
      const res = await teamService.getMemberTeamCollection({
        companyId: selectedCompany.value,
        userId: userUID,
        role: selectedCompanyRole,
        companyRole: selectedCompanyCompanyRole,
      });
      if (!res.success) {
        throw new Error(res.error || 'Failed to load departments');
      }
      return res.data || [];
    },
    enabled: Boolean(userUID && selectedCompany?.value),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const teams = useMemo(
    () => rawTeams.map((t) => ({ teamId: t.docId, teamName: t.Team })),
    [rawTeams]
  );

  useEmployeePageCompanyTeamUrlSync({
    companyOptions,
    teamOptions: [],
    selectedCompany,
    selectedTeam: null,
    setSelectedCompany,
    setSelectedTeam: noop,
    history,
    location,
    defaultCompanyId: activeCompanyId,
    searchTerm,
  });
  useUrlParamSync({
    location,
    paramKey: EMPLOYEE_PAGE_QUERY.search,
    setValue: setSearchTerm,
  });

  const memberQueries = useQueries(
    teams.map((t) => ({
      queryKey: queryKeys.teamMemberDetails.detail(t.teamId),
      queryFn: () => getTeamMemberDetails(database, t.teamId),
      enabled: Boolean(userUID && selectedCompany?.value && t.teamId),
      staleTime: 2 * 60 * 1000,
      refetchOnWindowFocus: false,
    }))
  );

  const tableRows = useMemo(() => {
    return teams.map((t, i) => {
      const q = memberQueries[i];
      const map = q?.data;
      const loadingRow = q?.isLoading;
      const err = q?.isError;
      return {
        teamId: t.teamId,
        teamName: t.teamName || t.teamId,
        memberCount: loadingRow || err ? null : countActiveMembers(map),
        memberNames: loadingRow || err ? [] : getActiveMemberNames(map),
        _loading: loadingRow,
      };
    });
  }, [teams, memberQueries]);

  const filteredRows = useMemo(() => {
    const term = (searchTerm || '').trim().toLowerCase();
    if (!term) return tableRows;
    return tableRows.filter((row) =>
      (row.teamName || '').toLowerCase().includes(term)
    );
  }, [tableRows, searchTerm]);

  const sortedRows = useMemo(
    () => sortRows(filteredRows, sortOption?.value),
    [filteredRows, sortOption]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortOption, selectedCompany?.value]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));
  const pagedRows = sortedRows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const totalDepartments = teams.length;
  const totalMembers = tableRows.reduce(
    (sum, row) => sum + (row.memberCount || 0),
    0
  );
  const avgTeamSize =
    totalDepartments > 0 ? Math.round(totalMembers / totalDepartments) : 0;

  const invalidateTeamListData = () => {
    queryClient.invalidateQueries(
      queryKeys.teamList.teamsForCompany(userUID, selectedCompany?.value)
    );
    teams.forEach((t) => {
      queryClient.invalidateQueries(
        queryKeys.teamMemberDetails.detail(t.teamId)
      );
    });
  };

  const goToEmployeeListForTeam = (row) => {
    if (!selectedCompany?.value || !row?.teamId) return;
    history.push({
      pathname: appRoutes().employeeList,
      search: mergeEmployeePageFiltersIntoSearch(location.search, {
        companyId: selectedCompany.value,
        teamId: row.teamId,
      }),
    });
  };

  const openEditTeam = (row) => {
    setEditingTeam({ teamId: row.teamId, teamName: row.teamName });
    setTeamModalOpen(true);
  };

  const teamsErrorMessage =
    teamsQueryError && teamsErrorObj
      ? teamsErrorObj.message || 'Failed to load departments'
      : null;

  const columns = [
    {
      id: 'department',
      name: 'Department Name',
      style: { width: '45%' },
      cellRenderer: (row) => (
        <DepartmentCell
          teamName={row.teamName}
          onClick={() => goToEmployeeListForTeam(row)}
        />
      ),
    },
    {
      id: 'members',
      name: 'Members',
      style: { width: '48%' },
      cellRenderer: (row) => (
        <MembersCell
          loading={row._loading}
          memberNames={row.memberNames}
          memberCount={row.memberCount}
          onClick={() => goToEmployeeListForTeam(row)}
        />
      ),
    },
    {
      id: 'actions',
      name: 'Actions',
      style: { width: '7%' },
      cellRenderer: (row) => (
        <Button
          icon={<FiEdit2 size={14} />}
          variant={buttonVariants.outline}
          ariaLabel="Edit department"
          onClick={() => openEditTeam(row)}
        />
      ),
    },
  ];

  if (!authState.loading && !isAdminUser) {
    return (
      <Container className="mt-4">
        <Alert color="danger">
          Access denied. Only Admin users can view the department list.
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <div className={styles.employeeListPage}>
        <div className={styles.employeeListContainer}>
          <div className={styles.employeeListHeader}>
            <div className={styles.employeeListDropdownContainer}>
              <PillDropdown
                icon={<BsBuildings />}
                label="Company"
                value={selectedCompany?.value ?? null}
                options={companyOptions}
                loading={loadingCompanies}
                placeholder="Select Company"
                onChange={(val) => {
                  setSelectedCompany(
                    companyOptions.find((o) => o.value === val) || null
                  );
                }}
              />
            </div>
            <div className={styles.employeeListHeaderActions}>
              <Button
                text="Invite Members"
                icon={<FiPlus />}
                onClick={() =>
                  history.push({
                    pathname: appRoutes().addEmployee,
                    search: mergeEmployeePageFiltersIntoSearch(
                      location.search,
                      {
                        companyId: selectedCompany?.value ?? '',
                        teamId: '',
                      }
                    ),
                  })
                }
                variant={buttonVariants.primary_rounded}
              />
              <Button
                text="Add Department"
                icon={<FiPlus />}
                onClick={() => {
                  setEditingTeam(null);
                  setTeamModalOpen(true);
                }}
                variant={buttonVariants.secondary}
                className={styles.headerActionButton}
              />
            </div>
          </div>

          <div className={styles.statsRow}>
            <StatCard
              icon={<BsBuildings />}
              iconBg="var(--blue-100)"
              label="Total Departments"
              value={totalDepartments}
            />
            <StatCard
              icon={<FiUsers />}
              iconBg="var(--purple-100)"
              label="Total Members"
              value={totalMembers}
            />
            <StatCard
              icon={<FiBarChart2 />}
              iconBg="var(--success-100)"
              label="Avg Team Size"
              value={avgTeamSize}
            />
          </div>

          <div className={styles.employeeListTableContainer}>
            {(companiesError || teamsErrorMessage) && (
              <Alert color="danger" className="mb-4">
                {companiesError || teamsErrorMessage}
              </Alert>
            )}

            {!selectedCompany ? (
              <EmptyState
                illustration
                title="Select a Company"
                description="Choose a company above to view its departments."
              />
            ) : loadingTeams ? (
              <div className="w-100 text-center py-5">
                <Spinner />
              </div>
            ) : teams.length === 0 ? (
              <EmptyState
                illustration
                title="No Departments Found"
                description="This company doesn't have any departments yet."
              />
            ) : (
              <>
                <div className={styles.tableToolbar}>
                  <div className={styles.toolbarSearch}>
                    <CustomInput
                      id="department-list-search"
                      type="text"
                      placeholder="Search by department name..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className={styles.toolbarActions}>
                    <Dropdown
                      options={SORT_OPTIONS}
                      value={sortOption}
                      onChange={setSortOption}
                      leftIcon={<FiSliders size={14} />}
                      width="190px"
                      size={dropdownSizes.sm}
                      isSearchable={false}
                    />
                  </div>
                </div>

                {sortedRows.length === 0 ? (
                  <EmptyState
                    illustration
                    title="No Matching Departments"
                    description="No departments match your search."
                  />
                ) : (
                  <>
                    <Table
                      className={styles.departmentsTable}
                      data={pagedRows}
                      columns={columns}
                    />
                    <div className={styles.tableFooter}>
                      <Text variant={textVariantsEnum.Text_sm} color="gray-500">
                        Showing {(currentPage - 1) * PAGE_SIZE + 1} to{' '}
                        {Math.min(currentPage * PAGE_SIZE, sortedRows.length)}{' '}
                        of {sortedRows.length} departments
                      </Text>
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <AddTeamModal
        isVisible={teamModalOpen}
        onClose={() => {
          setTeamModalOpen(false);
          setEditingTeam(null);
        }}
        callback={invalidateTeamListData}
        defaultCompanyId={selectedCompany?.value ?? activeCompanyId}
        companyId={editingTeam ? selectedCompany?.value : undefined}
        teamId={editingTeam?.teamId}
        initialTeamName={editingTeam?.teamName}
      />
    </>
  );
};

TeamListComponent.displayName = 'TeamList';
